// src/components/PrivacyPolicy.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import Header from "../../mainpagecomponents/Header";
import Footer from "../../mainpagecomponents/Footer";

import {
    Box,
    Container,
    Paper,
    Typography,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
    Link,
    Stack,
    IconButton,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import GavelIcon from "@mui/icons-material/Gavel";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";

const Bullet = ({ children }) => (
    <ListItem sx={{ alignItems: "flex-start", pl: 0 }}>
        <ListItemIcon sx={{ minWidth: 32, pt: "4px" }}>
            <CheckCircleOutlineIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
            primaryTypographyProps={{ sx: { lineHeight: 1.7, fontSize: 15 } }}
            primary={children}
        />
    </ListItem>
);

export default function PrivacyPolicy({ embed = false }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // Dark/light surfaces
    const surface = theme.palette.mode === "dark" ? "background.paper" : "#FDFCF7";

    // Section registry for TOC + scrollspy
    const sections = useMemo(
        () => [
            {
                id: "summary",
                title: "Plain Language Summary",
                icon: <InfoOutlinedIcon />,
            },
            {
                id: "collection",
                title: "1. Collection & Use of Student Information",
                icon: <GavelIcon />,
            },
            {
                id: "ai-usage",
                title: "2. AI Tool Usage & Restrictions",
                icon: <SmartToyOutlinedIcon />,
            },
            {
                id: "third-party",
                title: "3. Third‑Party AI Services",
                icon: <MenuBookOutlinedIcon />,
            },
            {
                id: "marketing",
                title: "4. No Third‑Party Marketing or Advertising",
                icon: <SecurityOutlinedIcon />,
            },
            {
                id: "your-data",
                title: "5. Your Data, Your Choice",
                icon: <DeleteOutlineIcon />,
            },
        ],
        []
    );

    const refs = useRef(Object.fromEntries(sections.map(s => [s.id, null])));
    const [activeId, setActiveId] = useState(sections[0].id);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                // Pick the top-most visible section
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible[0]?.target?.id) setActiveId(visible[0].target.id);
            },
            { rootMargin: "-20% 0px -70% 0px", threshold: [0, 0.25, 0.6, 1] }
        );

        sections.forEach(({ id }) => {
            const node = refs.current[id];
            if (node) observer.observe(node);
        });

        return () => observer.disconnect();
    }, [sections]);

    const scrollTo = (id) => {
        const el = refs.current[id];
        if (!el) return;


        // In embedded modal, avoid scrolling the whole window.
        if (!embed) {
            window.scrollTo({
                top: el.offsetTop - 88,
                behavior: "smooth",
            });
        }
    };

    const DateBar = (
        <Box sx={{ backgroundColor: surface, minHeight: "1vh", pt: 16, pb: 6, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Container maxWidth="lg" sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                <Chip size="small" color="primary" variant="outlined" icon={<ListAltOutlinedIcon />} label="Privacy Policy" />
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    <strong>Effective:</strong> Aug 7, 2025
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>•</Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    <strong>Last Updated:</strong> Aug 7, 2025
                </Typography>
            </Container>
        </Box>
    );

    const LinkStyled = (props) => (
        <Link
            {...props}
            underline="hover"
            sx={{
                color: "secondary.main",
                fontWeight: 600,
                outlineOffset: "2px",
                "&:focus-visible": {
                    outline: `2px solid ${theme.palette.secondary.main}`,
                    borderRadius: 1,
                },
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
            }}
        />
    );

    // Section wrappers: Paper on desktop; Accordion on mobile
    const SectionWrap = ({ id, icon, title, children }) => {
        if (isMobile) {
            return (
                <Accordion
                    defaultExpanded={id === "summary"}
                    sx={{
                        mb: 2,
                        borderRadius: 2,
                        overflow: "hidden",
                        bgcolor: theme.palette.background.paper,
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {icon}
                            <Typography variant="subtitle1" fontWeight={800}>{title}</Typography>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box id={id} ref={(n) => (refs.current[id] = n)}>{children}</Box>
                    </AccordionDetails>
                </Accordion>
            );
        }

        return (
            <Paper
                elevation={0}
                id={id}
                ref={(n) => (refs.current[id] = n)}
                sx={{
                    p: { xs: 2, md: 3 },
                    mb: 3,
                    borderRadius: 3,
                    bgcolor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    {icon}
                    <Typography variant="h6" fontWeight={800}>{title}</Typography>
                </Stack>
                {children}
            </Paper>
        );
    };

    return (
        <>
            {!embed && <Header />}



            <main>
                {!embed && DateBar}

                {DateBar}



                <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 } }}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 3, md: 4 }}>
                        {/* Content */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h4" fontWeight={800} sx={{ mb: 2 }}>
                                📄 Privacy Policy – Student Data & AI Usage
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                This page explains how Gradely handles student information and AI features, in language that’s readable and specific.
                            </Typography>

                            {/* Summary – always Paper for emphasis */}
                            <Paper
                                elevation={3}
                                sx={{
                                    p: { xs: 2, md: 3 },
                                    mb: 3,
                                    borderRadius: 3,
                                    bgcolor: theme.palette.mode === "dark" ? "background.default" : "#FFF",
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
                                    <InfoOutlinedIcon />
                                    <Typography variant="h6" fontWeight={800}>Plain Language Summary</Typography>
                                </Stack>
                                <List dense>
                                    <Bullet><strong>All student data is stored in Canada</strong> on secure Microsoft Azure servers.</Bullet>
                                    <Bullet><strong>No selling or advertising</strong> using student data.</Bullet>
                                    <Bullet>Only authorized staff can add student info, with <strong>parent/guardian consent</strong> where required.</Bullet>
                                    <Bullet><strong>You control your data</strong> — delete anytime; deletions are permanent.</Bullet>
                                    <Bullet><strong>Do not use student names/PII with AI tools.</strong></Bullet>
                                    <Bullet>AI inputs are processed by <strong>OpenAI</strong> under their policy, not Gradely’s.</Bullet>
                                    <Bullet>Gradely <strong>does not monitor or store</strong> what you enter into AI tools.</Bullet>
                                </List>
                            </Paper>

                            {/* Desktop sections (Accordion on mobile handled inside SectionWrap) */}
                            <SectionWrap id="collection" icon={<GavelIcon />} title="1. Collection & Use of Student Information">
                                <Typography sx={{ mb: 2 }}>
                                    Gradely lets authorized users manage classroom performance data. Information may include:
                                </Typography>
                                <List dense sx={{ mb: 2 }}>
                                    <Bullet>Student names</Bullet>
                                    <Bullet>Gender</Bullet>
                                    <Bullet>Grades and assessment results</Bullet>
                                </List>
                                <Typography fontWeight={700} sx={{ mb: 1 }}>Key protections:</Typography>
                                <List dense>
                                    <Bullet><strong>Stored exclusively on Microsoft Azure in Canada.</strong></Bullet>
                                    <Bullet>Compliance with <strong>PIPEDA</strong> and, where applicable, <strong>PIPA (BC)</strong>.</Bullet>
                                    <Bullet>Gradely acts as a <strong>data processor/service provider</strong>.</Bullet>
                                    <Bullet>Users are responsible for getting <strong>verifiable parent/guardian consent</strong> where required.</Bullet>
                                    <Bullet><strong>No selling</strong> or advertising with student data.</Bullet>
                                    <Bullet>Data may be <strong>aggregated/anonymized</strong> for internal analytics and improvement.</Bullet>
                                    <Bullet>Users can <strong>delete data at any time</strong>. Deletion is permanent.</Bullet>
                                </List>
                            </SectionWrap>

                            <SectionWrap id="ai-usage" icon={<SmartToyOutlinedIcon />} title="2. AI Tool Usage & Restrictions">
                                <Typography sx={{ mb: 2 }}>
                                    AI tools assist educators with generating materials and feedback.
                                </Typography>
                                <Typography fontWeight={700} sx={{ mb: 1 }}>Important restrictions:</Typography>
                                <List dense>
                                    <Bullet>AI tools are not designed for identifiable student information.</Bullet>
                                    <Bullet>Never enter student names or other PII into AI prompts.</Bullet>
                                    <Bullet>PII entered into AI is at the user’s own risk; Gradely isn’t responsible for retention or use by AI providers.</Bullet>
                                    <Bullet>Gradely does not monitor or retain AI inputs.</Bullet>
                                    <Bullet>Identifiable AI inputs are <strong>not covered</strong> by internal storage safeguards.</Bullet>
                                </List>
                            </SectionWrap>

                            <SectionWrap id="third-party" icon={<MenuBookOutlinedIcon />} title="3. Third‑Party AI Services">
                                <Typography sx={{ mb: 2 }}>
                                    We use <strong>OpenAI</strong> as our sole AI subprocessor.
                                </Typography>
                                <List dense>
                                    <Bullet>
                                        AI inputs are processed under OpenAI’s Privacy Policy:&nbsp;
                                        <LinkStyled href="https://openai.com/privacy" target="_blank" rel="noreferrer">
                                            openai.com/privacy <OpenInNewRoundedIcon fontSize="small" />
                                        </LinkStyled>
                                    </Bullet>
                                    <Bullet>
                                        By using AI tools in Gradely, you agree that <strong>AI inputs are governed by OpenAI’s terms</strong>, and that Gradely is <strong>not liable</strong> for misuse or disclosure of PII through AI inputs.
                                    </Bullet>
                                </List>
                            </SectionWrap>

                            <SectionWrap id="marketing" icon={<SecurityOutlinedIcon />} title="4. No Third‑Party Marketing or Advertising">
                                <Typography sx={{ mb: 2 }}>
                                    We do not share personal data with third parties for their marketing.
                                </Typography>
                                <List dense>
                                    <Bullet>No contextual ads or promotional content from third parties appear in Gradely.</Bullet>
                                    <Bullet>Data is only shared with service providers as needed to deliver requested services, consistent with this policy.</Bullet>
                                </List>
                            </SectionWrap>

                            <SectionWrap id="your-data" icon={<DeleteOutlineIcon />} title="5. Your Data, Your Choice">
                                <List dense>
                                    <Bullet>You control the data you enter.</Bullet>
                                    <Bullet>You can request deletion of your account or data at any time.</Bullet>
                                    <Bullet>
                                        Questions or deletion requests:{" "}
                                        <LinkStyled href="mailto:info@gradely.ca">info@gradely.ca</LinkStyled>
                                    </Bullet>
                                </List>
                            </SectionWrap>

                            {/* Back to top (mobile & desktop) */}
                            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                                <IconButton
                                    aria-label="Back to top"
                                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                                    sx={{
                                        border: `1px solid ${theme.palette.divider}`,
                                        bgcolor: theme.palette.background.paper,
                                        ":hover": { bgcolor: theme.palette.action.hover },
                                    }}
                                >
                                    <ArrowUpwardRoundedIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Sticky TOC (desktop only) */}
                        {!isMobile && (
                            <Box
                                sx={{
                                    width: 280,
                                    position: "sticky",
                                    top: 96, // below Header
                                    alignSelf: "flex-start",
                                    height: "fit-content",
                                }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        borderRadius: 3,
                                        border: `1px solid ${theme.palette.divider}`,
                                        bgcolor: theme.palette.background.paper,
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                        <ListAltOutlinedIcon />
                                        <Typography variant="subtitle1" fontWeight={800}>On this page</Typography>
                                    </Stack>
                                    <Divider sx={{ mb: 1 }} />
                                    <List dense sx={{ py: 0 }}>
                                        {sections.map((s) => (
                                            <ListItem
                                                key={s.id}
                                                disableGutters
                                                sx={{
                                                    px: 0,
                                                    my: 0.25,
                                                    cursor: "pointer",
                                                    borderRadius: 1.5,
                                                    bgcolor: activeId === s.id ? theme.palette.action.selected : "transparent",
                                                    transition: "background-color .15s",
                                                    "&:hover": { bgcolor: theme.palette.action.hover },
                                                }}
                                                onClick={() => scrollTo(s.id)}
                                            >
                                                <ListItemIcon sx={{ minWidth: 32 }}>{s.icon}</ListItemIcon>
                                                <ListItemText
                                                    primaryTypographyProps={{
                                                        fontSize: 14,
                                                        fontWeight: activeId === s.id ? 700 : 500,
                                                    }}
                                                    primary={s.title}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            </Box>
                        )}
                    </Stack>
                </Container>
            </main>
            {!embed && <Footer />}
        </>
    );
}
